import { Component, Input, OnChanges, SimpleChanges } from "@angular/core";
import { IJobHistoryAiSummaryModel } from "@models";
import { CustomersService } from "@services";

// The AI recap that sits above the appointment history. It renders nothing at all unless there is a
// summary to show, so a company with no AI configured - or a customer with barely any history - just
// sees the history list exactly as before.
@Component({
	selector: "ai-summary",
	templateUrl: "./ai-summary.component.html",
	styleUrls: ["./ai-summary.component.scss"],
	providers: [CustomersService]
})
export class AiSummaryComponent implements OnChanges {
	@Input() customerId: number;

	// The "Reviewing appointment history" panel only appears once a request has been in flight this
	// long. Every reply that carries no work (AI not configured, too little history, a summary already
	// cached) comes back well inside it, so the panel no longer flashes up and vanishes for a customer
	// with nothing to summarize. A real generation takes seconds, so it still shows for that.
	private static readonly LOADING_DELAY_MS = 500;

	summaryText: string = null;
	intro: string = null;
	bullets: string[] = [];
	// True only while a request has outlived LOADING_DELAY_MS, not for the whole request.
	isLoading: boolean = false;

	// Bumped on every refresh so a reply (or a loading timer) from a previous customer can't land on
	// the current one.
	private requestId: number = 0;

	constructor(private readonly customersService: CustomersService) { }

	async ngOnChanges(changes: SimpleChanges) {
		if (changes.customerId)
			await this.refresh();
	}

	async refresh() {
		const requestId = ++this.requestId;
		this.setSummary(null);
		this.isLoading = false;

		if (!this.customerId)
			return;

		const loadingTimer = setTimeout(() => {
			if (requestId === this.requestId)
				this.isLoading = true;
		}, AiSummaryComponent.LOADING_DELAY_MS);

		try {
			const summary: IJobHistoryAiSummaryModel = await this.customersService.getJobHistoryAiSummary(this.customerId);
			if (requestId === this.requestId)
				this.setSummary(summary?.summaryText);
		}
		catch {
			// Never let a missing summary break the history below it.
			if (requestId === this.requestId)
				this.setSummary(null);
		}
		finally {
			clearTimeout(loadingTimer);
			if (requestId === this.requestId)
				this.isLoading = false;
		}
	}

	// Splits the reply into the opening sentences and the "- " lines beneath them, so the list can be
	// rendered as a real <ul> instead of preformatted text. Written to degrade quietly: if the model
	// returns no bullets, or nothing but bullets, whatever came back still ends up on screen.
	private setSummary(text: string) {
		this.summaryText = text || null;
		this.intro = null;
		this.bullets = [];

		if (!this.summaryText)
			return;

		const introLines: string[] = [];

		for (const line of this.summaryText.split("\n").map(x => x.trim()).filter(x => x.length > 0)) {
			if (line.startsWith("- ") || line.startsWith("* ") || line.startsWith("• "))
				this.bullets.push(line.substring(2).trim());
			else if (this.bullets.length > 0)
				// A bullet that wrapped onto its own line rather than a new point.
				this.bullets[this.bullets.length - 1] += " " + line;
			else
				introLines.push(line);
		}

		this.intro = introLines.join(" ") || null;
	}
}
